// src/components/AdsModal.jsx
import React, { useCallback, useMemo, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, Button, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox } from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { useAPI } from "../context/ApiContext";
import { useTranslation } from "react-i18next";

const IMP_PLANS = [
  { label: "Impression Basic (5,000)", value: 5000 },
  { label: "Impression Standard (50,000)", value: 50000 },
  { label: "Impression Premium (100,000)", value: 100000 },
];
const CLK_PLANS = [
  { label: "Click Basic (1,000)", value: 1000 },
  { label: "Click Standard (3,000)", value: 3000 },
  { label: "Click Premium (10,000)", value: 10000 },
];
const MAX_CONTENT = 210;

const AdsModal = ({ isVisible, handleClose }) => {
  const { t } = useTranslation();
  const { createAd, checkoutAd } = useAPI();

  const [billing, setBilling] = useState("imp"); // "imp" or "clk"
  const [plan, setPlan] = useState(IMP_PLANS[0].value);
  const [url, setUrl] = useState("");
  const [content, setContent] = useState("");
  const [imgFile, setImgFile] = useState(null);
  const [imgPreview, setImgPreview] = useState("");
  const [agree, setAgree] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const fileRef = useRef(null);

  const onFileChange = useCallback((e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImgFile(f);
    const reader = new FileReader();
    reader.onload = () => setImgPreview(reader.result?.toString() || "");
    reader.readAsDataURL(f);
  }, []);

  const canSubmit = useMemo(() => {
    if (!agree) return false;
    if (!url.trim()) return false;
    if (!imgFile) return false;
    return true;
  }, [agree, url, imgFile]);

  const plans = billing === "imp" ? IMP_PLANS : CLK_PLANS;

  const resetForm = () => {
    setBilling("imp");
    setPlan(IMP_PLANS[0].value);
    setUrl("");
    setContent("");
    setImgFile(null);
    setImgPreview("");
    setAgree(false);
    setProcessing(false);
    setErrorMsg("");
  };

  const submit = async () => {
    if (!canSubmit) return;
    setProcessing(true);
    setErrorMsg("");
    try {
      // 1) 作成（画像＋URLのみ）
      const created = await createAd({
        url: url.trim(),
        content: content.trim(),
        img: imgFile,
        billing,
        target: plan,
      });

      // 2) Stripe Checkout → リダイレクト
      const SITE_URL = `${window.location.origin}/adapplication`;
      const res = await checkoutAd({
        SITE_URL,
        data: { id: created?.id, billing, target: plan },
      });

      if (res?.goTo) {
        window.location.href = res.goTo;
        return;
      }
      throw new Error("No checkout URL");
    } catch (e) {
      setErrorMsg(e?.data?.detail || e?.message || "Failed to create advertisement");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog className="animated fadeIn" open={isVisible} onClose={() => { resetForm(); handleClose(); }} fullWidth maxWidth="sm" aria-labelledby="ads-modal-title">
      <DialogTitle id="ads-modal-title" className="mycard bg-current">
        <span className="fw-600 text-white font-xs">{t("AdsModal1") || "広告出稿"}</span>
      </DialogTitle>

      <DialogContent className="mycard card">
        {errorMsg && <Alert severity="error" className="mb-2">{errorMsg}</Alert>}

        {/* 課金方式 / プラン */}
        <div className="row mb-3">
          <div className="col-md-6 col-sm-12">
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>課金方式</InputLabel>
              <Select
                label="課金方式"
                value={billing}
                onChange={(e) => {
                  const v = e.target.value;
                  setBilling(v);
                  setPlan((v === "imp" ? IMP_PLANS : CLK_PLANS)[0].value);
                }}
              >
                <MenuItem value="imp">Impression</MenuItem>
                <MenuItem value="clk">Click</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div className="col-md-6 col-sm-12">
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>プラン</InputLabel>
              <Select label="プラン" value={plan} onChange={(e) => setPlan(Number(e.target.value))}>
                {(plans).map((p) => (
                  <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>

        {/* URL（必須） */}
        <div className="form-group">
          <label className="mont-font fw-600 font-xsss mb-1 text-grey-600">リンク先URL <span className="text-danger">*</span></label>
          <TextField value={url} type="url" onChange={(e) => setUrl(e.target.value)} variant="outlined" size="small" fullWidth placeholder="https://example.com" />
        </div>

        {/* 本文（任意） */}
        <div className="form-group mt-2">
          <label className="mont-font fw-600 font-xsss mb-1 text-grey-600">本文（任意）</label>
          <TextField
            value={content}
            onChange={(e) => setContent(e.target.value.slice(0, MAX_CONTENT))}
            placeholder={`（最大${MAX_CONTENT}文字）`}
            multiline rows={3} variant="outlined" size="small" fullWidth
            helperText={`${content.length}/${MAX_CONTENT}`}
          />
        </div>

        {/* 画像（必須） */}
        <div className="row mt-2">
          <div className="col-md-6 col-sm-12 mb-2 d-flex justify-content-center">
            {imgPreview ? (
              <img src={imgPreview} alt="preview" style={{ borderRadius: 12, padding: 2, maxHeight: 130 }} />
            ) : (
              <div className="card d-flex full-width justify-content-center align-items-center font-xsss border-light-md shadow-xss text-grey-600" style={{ borderRadius: 12, padding: 2, minHeight: 100, width: "100%" }}>
                画像をアップロードしてください
              </div>
            )}
          </div>
          <div className="col-md-6 col-sm-12">
            <Button className="full-width bg-current mt-0 btn pt-2 pb-2 ps-3 pe-3 lh-24 d-inline-block rounded-4 font-xsssss fw-700 text-white" variant="contained" fullWidth component="label">
              画像アップロード <span className="text-danger font-xs"> *</span>
              <input ref={fileRef} type="file" hidden accept="image/*" onChange={onFileChange} />
            </Button>
          </div>
        </div>

        {/* 同意 */}
        <div className="mt-3">
          <FormControlLabel
            control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
            label={<span className="font-xsss text-grey-600">
              <a href="https://tatami.tokyo/pricing/" target="_blank" rel="noreferrer" className="text-decoration-underline">料金表</a>{" / "}
              <a href="https://tatami.tokyo/terms-conditions-ad/" target="_blank" rel="noreferrer" className="text-decoration-underline">広告規約</a>{" "}に同意する <span className="text-danger">*</span>
            </span>}
          />
        </div>

        {/* 送信 */}
        <div className="mt-2">
          <Button onClick={submit} disabled={!canSubmit || processing} className="full-width bg-current mt-0 btn pt-2 pb-2 ps-3 pe-3 lh-24 d-inline-block rounded-4 font-xsssss fw-700 text-white" variant="contained" fullWidth>
            {processing ? "処理中…" : "決済へ進む"}
          </Button>
          {!canSubmit && (
            <div className="text-danger font-xssss mt-2">
              {!imgFile ? "画像は必須です" : !url.trim() ? "リンク先URLは必須です" : !agree ? "規約への同意が必要です" : ""}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdsModal;
